import React, { useState, useEffect, useContext } from "react";
import { useStyle } from "../../context/styleContext/styleContext";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SubscriptionsRoundedIcon from "@mui/icons-material/SubscriptionsRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { MuiTelInput } from "mui-tel-input";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ChangePasswordModal from "./ChangePasswordModal";
import { UserContext } from "../../context/userContext/userContext";
import Seo from "../../components/Seo";

export default function Account() {
  const { setUserEmail } = useContext(UserContext);
  const { style } = useStyle();
  const navigate = useNavigate();

  const countryList = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina",
    "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh",
    "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia",
    "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso",
    "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic",
    "Chad", "Chile", "China", "Colombia", "Comoros", "Congo (Congo-Brazzaville)",
    "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba", "Cyprus", "Czechia (Czech Republic)",
    "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
    "Eswatini (fmr. \"Swaziland\")", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
    "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea",
    "Guinea-Bissau", "Guyana", "Haiti", "Holy See", "Honduras", "Hungary", "Iceland", "India",
    "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan",
    "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
    "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar",
    "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania",
    "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro",
    "Morocco", "Mozambique", "Myanmar (formerly Burma)", "Namibia", "Nauru", "Nepal",
    "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea",
    "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine State", "Panama",
    "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
    "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
    "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe",
    "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore",
    "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
    "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
    "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga",
    "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
    "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
    "Vanuatu", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe",
  ];

  const emptyForm = {
    userName: "",
    email: "",
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: "",
    okToEmail: false,
  };

  const [originalData, setOriginalData] = useState(emptyForm);
  const [formData, setFormData] = useState(emptyForm);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [userNameAlert, setUserNameAlert] = useState("");
  const [emailAlert, setEmailAlert] = useState("");
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);

  const openPasswordModal = () => setPasswordModalOpen(true);
  const closePasswordModal = () => setPasswordModalOpen(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    getAccountInfo();
  }, []);

  const navToLogin = () => {
    setPageIsLoading(false);
    navigate("/login");
  };

  const navToSubscriptions = () => {
    setPageIsLoading(false);
    navigate("/manage-subscriptions");
  };

  const isUnchanged =
    originalData.userName === formData.userName &&
    originalData.firstName === formData.firstName &&
    originalData.lastName === formData.lastName &&
    originalData.address1 === formData.address1 &&
    originalData.address2 === formData.address2 &&
    originalData.city === formData.city &&
    originalData.state === formData.state &&
    originalData.zip === formData.zip &&
    originalData.country === formData.country &&
    originalData.phone === formData.phone &&
    originalData.okToEmail === formData.okToEmail;

  const getAccountInfo = async () => {
    setPageIsLoading(true);

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        navToLogin();
        return;
      }

      const { data } = await axios.post("/accountinfo", { token });

      if (data.error) {
        toast.error(data.error);
        setPageIsLoading(false);
        return;
      }

      const loadedData = {
        userName: data.userName || "",
        email: data.email || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        address1: data.address1 || "",
        address2: data.address2 || "",
        city: data.city || "",
        state: data.state || "",
        zip: data.zip || "",
        country: data.country || "",
        phone: data.phone || "",
        okToEmail: !!data.okToEmail,
      };

      setOriginalData(loadedData);
      setFormData(loadedData);
    } catch (error) {
      console.log(error);
      toast.error("Unable to load account information.");
    } finally {
      setPageIsLoading(false);
    }
  };

  const submitChanges = async (e) => {
    e.preventDefault();
    setPageIsLoading(true);
    setUserNameAlert("");
    setEmailAlert("");

    const token = localStorage.getItem("token");

    if (!token) {
      navToLogin();
      return;
    }

    const dataToSend = {
      token,
      userName: formData.userName,
      email: formData.email, // keep sending current email so backend validation passes
      firstName: formData.firstName,
      lastName: formData.lastName,
      address1: formData.address1,
      address2: formData.address2,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: formData.country,
      phone: formData.phone,
      okToEmail: formData.okToEmail,
    };

    try {
      const response = await axios.patch("/account", dataToSend);
      const data = response.data;

      if (formData.email) {
        await setUserEmail(formData.email.toLowerCase());
      }

      if (data.error) {
        toast.error(data.error);
        setPageIsLoading(false);
        return;
      }

      setOriginalData({ ...formData });
      localStorage.setItem("token", data.token);
      toast.success("Account information updated!");
    } catch (error) {
      if (error.response) {
        const field = error.response.data.errorField;

        if (field === "email") {
          setEmailAlert(error.response.data.error);
        }

        if (field === "userName") {
          setUserNameAlert(error.response.data.error);
        }

        toast.error(error.response.data?.error || "Update failed");
      } else {
        toast.error("Update failed");
      }
    } finally {
      setPageIsLoading(false);
    }
  };

  const changeUserName = (e) => {
    setUserNameAlert("");
    handleChange(e);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePhoneChange = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      phone: newValue,
    }));
  };

  const sharedTextFieldSx = {
    width: "100%",
    minWidth: 0,
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: "8px",
      minWidth: 0,
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-root": {
      minWidth: 0,
      "& fieldset": {
        borderColor: style.color2,
      },
      "&:hover fieldset": {
        borderColor: style.color3,
      },
      "&.Mui-focused fieldset": {
        borderColor: style.accentGreen,
      },
      "&.Mui-disabled": {
        borderColor: style.color2,
        color: style.color3,
      },
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: style.color3,
      color: style.color3,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      color: style.color3,
    },
    "input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0px 1000px ${style.color2} inset`,
      WebkitTextFillColor: style.color4,
      caretColor: style.color4,
      borderRadius: "8px",
    },
  };

  const sectionCardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: "14px",
    backgroundColor: style.darkPanel,
    border: "1px solid rgba(255,255,255,0.08)",
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  };

  const gridItemSx = {
    minWidth: 0,
  };

  return (
    <>
      <Seo
        title="Account | Parish Audio"
        description="Manage your Parish Audio account information, purchases, subscriptions, and plugin access."
        canonical="https://parishaudio.com/account"
        robots="noindex,follow"
      />

      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          color: style.color4,
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
        }}
      >
        {pageIsLoading ? (
          <LoadingSpinner />
        ) : (
          <Box
            sx={{
              maxWidth: 1100,
              width: "100%",
              minWidth: 0,
              mx: "auto",
              boxSizing: "border-box",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: { xs: 2.25, md: 3 },
                  backgroundColor: style.darkPanel,
                  borderBottom: `1px solid ${style.color2}`,
                }}
              >
                <Stack spacing={1} alignItems="flex-start">
                  <Chip
                    icon={<PersonRoundedIcon />}
                    label="Account"
                    sx={{
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                    }}
                  />

                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: { xs: "1.9rem", md: "2.5rem" },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    My Account
                  </Typography>

                  <Typography
                    sx={{
                      color: style.color3,
                      maxWidth: 720,
                      lineHeight: 1.65,
                    }}
                  >
                    Update your account details, contact information, and preferences.
                  </Typography>
                </Stack>
              </Box>

              <Box
                component="form"
                onSubmit={submitChanges}
                sx={{
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: { xs: 1.5, md: 2.5 },
                  width: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    m: 0,
                  }}
                >
                  <Grid item xs={12} lg={8} sx={gridItemSx}>
                    <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
                      <Paper elevation={0} sx={sectionCardSx}>
                        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PersonRoundedIcon sx={{ color: style.color3 }} />
                            <Typography
                              sx={{
                                color: style.color4,
                                fontWeight: 800,
                                fontSize: "1.05rem",
                              }}
                            >
                              Account Details
                            </Typography>
                          </Stack>

                          <Grid container spacing={2} sx={{ width: "100%", minWidth: 0, m: 0 }}>
                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="userName"
                                label="Username"
                                size="small"
                                value={formData.userName}
                                onChange={changeUserName}
                                sx={sharedTextFieldSx}
                              />
                              {!!userNameAlert && (
                                <Typography
                                  sx={{
                                    color: "#ef9a9a",
                                    fontSize: "0.82rem",
                                    mt: 0.75,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {userNameAlert}
                                </Typography>
                              )}
                            </Grid>

                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="email"
                                label="Email"
                                size="small"
                                value={formData.email}
                                disabled
                                sx={sharedTextFieldSx}
                              />
                              {!!emailAlert && (
                                <Typography
                                  sx={{
                                    color: "#ef9a9a",
                                    fontSize: "0.82rem",
                                    mt: 0.75,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {emailAlert}
                                </Typography>
                              )}
                            </Grid>

                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="firstName"
                                label="First Name"
                                size="small"
                                value={formData.firstName}
                                onChange={handleChange}
                                sx={sharedTextFieldSx}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="lastName"
                                label="Last Name"
                                size="small"
                                value={formData.lastName}
                                onChange={handleChange}
                                sx={sharedTextFieldSx}
                              />
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>

                      <Paper elevation={0} sx={sectionCardSx}>
                        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <HomeRoundedIcon sx={{ color: style.color3 }} />
                            <Typography
                              sx={{
                                color: style.color4,
                                fontWeight: 800,
                                fontSize: "1.05rem",
                              }}
                            >
                              Billing & Address
                            </Typography>
                          </Stack>

                          <Grid container spacing={2} sx={{ width: "100%", minWidth: 0, m: 0 }}>
                            <Grid item xs={12} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="address1"
                                label="Address 1"
                                size="small"
                                value={formData.address1}
                                onChange={handleChange}
                                sx={sharedTextFieldSx}
                              />
                            </Grid>

                            <Grid item xs={12} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="address2"
                                label="Address 2"
                                size="small"
                                value={formData.address2}
                                onChange={handleChange}
                                sx={sharedTextFieldSx}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="city"
                                label="City"
                                size="small"
                                value={formData.city}
                                onChange={handleChange}
                                sx={sharedTextFieldSx}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="state"
                                label="State / Province"
                                size="small"
                                value={formData.state}
                                onChange={handleChange}
                                sx={sharedTextFieldSx}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <TextField
                                fullWidth
                                name="zip"
                                label="Zip / Postal Code"
                                size="small"
                                value={formData.zip}
                                onChange={handleChange}
                                sx={sharedTextFieldSx}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} sx={gridItemSx}>
                              <FormControl fullWidth size="small" sx={sharedTextFieldSx}>
                                <InputLabel>Country</InputLabel>
                                <Select
                                  name="country"
                                  value={formData.country}
                                  label="Country"
                                  onChange={handleChange}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        maxWidth: "90vw",
                                      },
                                    },
                                  }}
                                >
                                  {countryList.map((country) => (
                                    <MenuItem
                                      key={country}
                                      value={country}
                                      sx={{ whiteSpace: "normal" }}
                                    >
                                      {country}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sx={gridItemSx}>
                              <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
                                <MuiTelInput
                                  fullWidth
                                  label="Phone Number"
                                  defaultCountry="US"
                                  size="small"
                                  value={formData.phone}
                                  onChange={handlePhoneChange}
                                  sx={{
                                    width: "100%",
                                    minWidth: 0,
                                    "& .MuiInputBase-root": {
                                      color: style.color4,
                                      backgroundColor: style.color2,
                                      borderRadius: "8px",
                                      minWidth: 0,
                                      boxSizing: "border-box",
                                    },
                                    "& .MuiInputLabel-root": {
                                      color: style.color3,
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      color: style.color4,
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: style.color2,
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: style.color3,
                                    },
                                    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: style.accentGreen,
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      minWidth: 0,
                                    },
                                    "& .MuiTelInput-TextField": {
                                      minWidth: 0,
                                    },
                                    "input:-webkit-autofill": {
                                      WebkitBoxShadow: `0 0 0px 1000px ${style.color2} inset`,
                                      WebkitTextFillColor: style.color4,
                                      caretColor: style.color4,
                                    },
                                  }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>

                      <Paper elevation={0} sx={sectionCardSx}>
                        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <EmailRoundedIcon sx={{ color: style.color3 }} />
                            <Typography
                              sx={{
                                color: style.color4,
                                fontWeight: 800,
                                fontSize: "1.05rem",
                              }}
                            >
                              Preferences
                            </Typography>
                          </Stack>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.okToEmail}
                                onChange={handleChange}
                                name="okToEmail"
                                sx={{
                                  color: style.color3,
                                  "&.Mui-checked": {
                                    color: style.accentGreen,
                                  },
                                }}
                              />
                            }
                            label="Yes, please email me with promotions and discounts"
                            sx={{
                              color: style.color4,
                              alignItems: "flex-start",
                              "& .MuiFormControlLabel-label": {
                                lineHeight: 1.5,
                              },
                            }}
                          />
                        </Stack>
                      </Paper>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} lg={4} sx={gridItemSx}>
                    <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
                      <Paper elevation={0} sx={sectionCardSx}>
                        <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <SaveRoundedIcon sx={{ color: style.color3 }} />
                            <Typography
                              sx={{
                                color: style.color4,
                                fontWeight: 800,
                                fontSize: "1.05rem",
                              }}
                            >
                              Save Changes
                            </Typography>
                          </Stack>

                          <Typography
                            sx={{
                              color: style.color3,
                              lineHeight: 1.6,
                              fontSize: "0.92rem",
                            }}
                          >
                            Update your account information and save your changes when you’re ready.
                          </Typography>

                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isUnchanged || pageIsLoading}
                            startIcon={<SaveRoundedIcon />}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: style.accentGreen,
                              width: "100%",
                              minWidth: 0,
                              "&:hover": {
                                backgroundColor: style.accentGreen,
                                filter: "brightness(1.05)",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(255,255,255,0.45)",
                                backgroundColor: "rgba(255,255,255,0.08)",
                              },
                            }}
                          >
                            Save Changes
                          </Button>

                          {isUnchanged && (
                            <Typography
                              sx={{
                                color: style.color3,
                                fontSize: "0.82rem",
                              }}
                            >
                              No unsaved changes.
                            </Typography>
                          )}
                        </Stack>
                      </Paper>

                      <Paper elevation={0} sx={sectionCardSx}>
                        <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <SecurityRoundedIcon sx={{ color: style.color3 }} />
                            <Typography
                              sx={{
                                color: style.color4,
                                fontWeight: 800,
                                fontSize: "1.05rem",
                              }}
                            >
                              Security & Billing
                            </Typography>
                          </Stack>

                          <Typography
                            sx={{
                              color: style.color3,
                              lineHeight: 1.6,
                              fontSize: "0.92rem",
                            }}
                          >
                            Manage your subscription or update your account password.
                          </Typography>

                          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                          <Button
                            variant="outlined"
                            onClick={navToSubscriptions}
                            startIcon={<SubscriptionsRoundedIcon />}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              borderColor: "rgba(255,255,255,0.12)",
                              width: "100%",
                              minWidth: 0,
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.06)",
                                borderColor: "rgba(255,255,255,0.18)",
                              },
                            }}
                          >
                            Manage Subscriptions
                          </Button>

                          <Button
                            variant="outlined"
                            onClick={openPasswordModal}
                            startIcon={<LockRoundedIcon />}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              borderColor: "rgba(255,255,255,0.12)",
                              width: "100%",
                              minWidth: 0,
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.06)",
                                borderColor: "rgba(255,255,255,0.18)",
                              },
                            }}
                          >
                            Change Password
                          </Button>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        close={closePasswordModal}
      />
    </>
  );
}




// import {React, useState, useContext, useEffect} from "react"
// import{UserContext} from '../../context/userContext/userContext'
// import { useStyle } from '../../context/styleContext/styleContext';
// import axios from 'axios'
// import {toast} from 'react-hot-toast'
// import Button from '@mui/material/Button';
// import ReactGA from 'react-ga4';
// //import { FOCUSABLE_SELECTOR } from "@testing-library/user-event/dist/utils";
// import ChangePasswordModal from "./ChangePasswordModal";
// import ChangeStripeEmailModal from "./ChangeStripeEmailModal";
// //import Terms from "../Terms/Terms"
// import { Checkbox, TextField, FormGroup, FormControlLabel } from "@mui/material";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";



// export default function Account(){

//   const { user } = useContext(UserContext);
//   const { style, setStyle } = useStyle();
//   const [isPageLoading, setIsPageLoading] = useState(false);

//   const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
//   const openPasswordModal = () => setPasswordModalOpen(true);
//   const closePasswordModal = () => setPasswordModalOpen(false);

//   const [isStripeEmailModalOpen, setStripeEmailModalOpen] = useState(false);
//   const openStripeEmailModal = () => setStripeEmailModalOpen(true);
//   const closeStripeEmailModal  = () => setStripeEmailModalOpen(false);

//   useEffect(() => {getAccountInfo();}, []); // pull list when screen loads
//   useEffect(() => {update();}, [])

//   const [height, setHeight] = useState(window.innerHeight)
//   const [width, setWidth] = useState(window.setWidth)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })



//     ReactGA.send({
//         hitType:"pageview",
//         page:"account",
//         title:"account",
//     })

//     useEffect(() => {
//     document.title = 'Parish Audio | State Of The Art State Of The Art Audio Processing | Account';
//     }, []);
  
//     // const [userData, setUserData] = useState({
//     // email: "",
//     // firstName: "",
//     // lastName: "",
//     // okToEmail: false,    
//     // })

//     let userToken = ""
//     if(localStorage.getItem('token'))
//     {
//         userToken = localStorage.getItem('token')
//     }

//     const [formData, setFormData] = useState({
//     email:"",
//     firstName: "",
//     lastName: "",
//     okToEmail: false,
//     token:userToken,
//     })

//     const [originalData, setOriginalData] = useState({
//         email:"",
//         firstName: "",
//         lastName: "",
//         okToEmail: false,
//         token:userToken,
//         })
    
//     const [imgData, setImgData] = useState({
//         profileImage: "",
//         })



//     const handleSubmit = async (e) => {
//         e.preventDefault();        
//     }


//     const saveNewImgUriToDB = async (imgURI) => {

//         console.log("submit!")

//         console.log('imgURI: ', imgURI)
    
//         try {
//             const { data } = await axios.patch(`/update_profile_pic/${imgURI}`);
//             if (data.error) {
//                 toast.error(data.error);
//             } else {
    
//                 await getAccountInfo();
//                 edit.activated = false
//                 toast.success('Account Information Updated!');
//             }
//         } catch (error) {
//             console.error(error);
//             toast.error('Update failed');
//         }
//     };

//     const [edit, setEdit] = useState({
//         activated : false
//     })

    
//   const changeBackground = () => {
//     setStyle({ ...style, backgroundColor: 'rgb(0,255,0)' });
//   };

//   const fire = () => {
//     console.log("FIRE!!!!!!")
//     console.log("form data x")
//     console.log(formData)
//     console.log("O Data: ")
//     console.log(originalData)
//   };

//   const toggleEditMode = async (e) => {
//     if(edit.activated){
//         setEdit({ ...edit, activated: false });
//     }
//     else{
//         setEdit({ ...edit, activated: true });
//     }


//   }

//   const cancel = async (e) => {

//     if(edit.activated){
//         setEdit({ ...edit, activated: false });
//     }
//     else{
//         setEdit({ ...edit, activated: true });
//     }
//   }

//   const update = async (e) => {

//   } 
  


//   const getAccountInfo = async (e) => {   
    
//     setIsPageLoading(true)

//     try{

//         const token = localStorage.getItem('token')
//         const dataToSend = {token: token}

//         if(token)
//         {

//         const{data} = await axios.post('/accountinfo', dataToSend)

//         if(data.error){
//             toast.error(data.error)
//         }
//         else{

//             const newFormData = 
//             {
//                 email:data.email,
//                 firstName:data.firstName,
//                 lastName:data.lastName,
//                 okToEmail:data.okToEmail,
//                 token:token
//             }

//             await setFormData(newFormData)
//             await setOriginalData(newFormData)    
//             await setIsPageLoading(false)
//         }


//     }
    

//     }catch(error){
//         console.log(error)
//     }
// }




//   const submitChanges = async (e) => {
    
//     e.preventDefault();
//     await setIsPageLoading(true);

//     console.log("submitting changes")
//     console.log("form data:")
//     console.log(formData)


//     try {
//         const { data } = await axios.patch('/account', formData);
//         if (data.error) {
//             toast.error(data.error);
//         } else {
            
//             const newFormData = 
//             {
//                 email:formData.email,
//                 firstName:formData.firstName,
//                 lastName:formData.lastName,
//                 okToEmail:formData.okToEmail,
//                 token:formData.token,
//             }

//             await setFormData(newFormData)
//             await setOriginalData(newFormData)    
//             await setIsPageLoading(false)
//             toast.success('Account Information Updated!');

//         }
//     } catch (error) {
//         console.error(error);
//         //setIsPageLoading(false);
//         toast.error('Update failed');
//     }
// };

//     return(
//         <>          

//     {isPageLoading &&(<div style={{minHeight:height - 130, alignContent:'center'}}><LoadingSpinner /></div>)}
//     {!isPageLoading &&(


// <div style={{display:'flex', flexDirection:'column', width:'100%'}}>

// <div style={{display:'flex', flexDirection:'column', width:'90%', maxWidth:'600px', margin:'auto', color:style.color1, textAlign:'center', marginTop:'40px', backgroundColor:style.color4, borderRadius:'10px', borderStyle:'solid', borderColor:style.color1}}>
//     <div style={{width:'90%', margin:'auto'}}>

    
//         <div style={{display:'flex', flex:1, textAlign:'center', marginTop:'20px'}}>
//             <h1 style={{flex:1, textAlign:'center', margin:'auto', marginBottom:'40px'}}>
//                 Account Information
//             </h1>
           
//         </div>

//         <div style={{maxWidth:'400px', margin:'auto'}}>
//         <div style={{display:'flex', flex:1, textAlign:'center'}}>
//             <h4 style={{flex:1, textAlign:'left', margin:'auto', color:style.color1}}>
//                 <div style={{marginBottom:'5px'}}>
                
//                 <TextField
//                 id="outlined-basic"
//                 label="Email"
//                 variant="outlined"
//                 value={formData.email}
//                 size='small'
//                 fullWidth
//                 fontSize='14px'
//                 sx={{
//                     backgroundColor: style.color3,
//                     '& .MuiInputBase-input': {
//                     color: style.color1,  // Change the font color here
//                     fontSize: '14px',  // Change the font size here
//                     },
//                     '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                         color: style.color1,
//                         borderColor: style.color1, // Change the border color if needed
//                         borderWidth:'2px'
//                     },
//                     '&:hover fieldset': {
//                         borderColor: style.color1, // Change the border color on hover if needed
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color1, // Change the border color when focused if needed
//                     },
//                     '&.Mui-disabled': {
//                         '& fieldset': {
//                         borderColor: style.color1, // Change the border color when disabled
//                         backgroundColor: style.color3
//                         },
//                     },
//                     },
//                     '& .MuiInputLabel-root': {
//                     color: style.color1, // Change the label color here
//                     fontSize:'14pt',
//                     },
//                     '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color1, // Change the label color when focused here
//                     },
//                     '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color1, // Change the label color when disabled
//                     },
//                     '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                         color: style.color1, // Change the input font color when disabled // so it works in safari
//                         WebkitTextFillColor: style.color1, // so it works on chrome
//                         fontSize:'14pt'
//                     },
//                     },
//                     '& .MuiOutlinedInput-input': { color: style.color1,
//                     '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px rgb(157, 178, 191) inset',
//             WebkitTextFillColor: style.color1, color:style.color1
//          }, }, 
//                 }}
//                 />

//                 </div>
//                 </h4>
//         </div>
//         <div style={{display:'flex', flex:1, textAlign:'center', marginBottom:'10px'}}> 
//         <FormGroup>
//             <FormControlLabel checked={formData.okToEmail} onChange={(e) => setFormData({ ...formData, okToEmail: e.target.checked }) } control={<Checkbox size='large' sx={{color:style.color1, '&.Mui-checked': {
//                 color: style.color1,
//             },}} />} label="Subscribe to Emails" />
//         </FormGroup>
//         </div>
//         <div style={{display:'flex', flex:1, textAlign:'center', padding:'0px', justifyContent:'top', margin:'auto', marginBottom:'50px'}}>
//                 <Button onClick={setPasswordModalOpen.bind(null, true)} sx={{ my: 2, color:style.color4, display:'block', backgroundColor:style.color2, margin:'auto', fontSize:'14pt', width:'100%', minWidth:'250px', maxWidth:'250px', padding:'5px', borderColor:style.color1, borderWidth:'2px', borderStyle:'solid', '&:hover': {backgroundColor: style.color3 }, }}>Change Password</Button>
//         </div>
//         <div style={{display:'flex', flex:1, textAlign:'center', marginBottom:'20px'}}>
//         <TextField
//                 id="outlined-basic"
//                 label="First Name"
//                 variant="outlined"
//                 value= {formData.firstName}
//                 onChange={(e) => setFormData({...formData, firstName: e.target.value})}
//                 size='small'
//                 fullWidth
//                 sx={{
//                     backgroundColor: style.color3,
//                     borderRadius:'4px',
//                     '& .MuiInputBase-input': {
//                     color: style.color1,  // Change the font color here
//                     fontSize: '14pt',  // Change the font size here
                                                    
//                     },
//                     '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                         borderColor: style.color1, // Change the border color if needed
//                         borderWidth:'2px'
//                     },
//                     '&:hover fieldset': {
//                         borderColor: style.color1, // Change the border color on hover if needed\
//                         borderWidth:'3px',                                                               
                 
                        
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color1, // Change the border color when focused if needed
//                         borderWidth:'3px'
                        
//                     },
//                     '&.Mui-disabled': {
//                         '& fieldset': {
//                         borderColor: style.color1, // Change the border color when disabled
//                         },
//                     },
//                     },
//                     '& .MuiInputLabel-root': {
//                     color: style.color2, // Change the label color here
//                     fontSize:'14pt',
//                     },
//                     '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color1, // Change the label color when focused here                                 
//                     },
//                     '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color1, // Change the label color when disabled
//                     },
//                     '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                         color: style.color1, // Change the input font color when disabled // so it works in safari
//                         WebkitTextFillColor: style.color1, // so it works on chrome
//                         fontSize:'14pt'
//                     },
//                     },
//                     '& .MuiOutlinedInput-input': { color: style.color1,
//         '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px rgb(157, 178, 191) inset',
//             WebkitTextFillColor: style.color1, color:style.color1
//          }, }, 
//                 }}
//                 />
//         </div>

//         <div style={{display:'flex', flex:1, textAlign:'center', marginBottom:'15px'}}>
//         <TextField
//                 id="outlined-basic"
//                 label="Last Name"
//                 variant="outlined"
//                 value= {formData.lastName}
//                 onChange={(e) => setFormData({...formData, lastName: e.target.value})}
//                 size='small'
//                 fullWidth
//                 sx={{
//                     backgroundColor: style.color3,
//                     borderRadius:'4px',
//                     '& .MuiInputBase-input': {
//                     color: style.color1,  // Change the font color here
//                     fontSize: '14pt',  // Change the font size here
                    
//                     },
//                     '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                         borderColor: style.color1, // Change the border color if needed
//                         borderWidth:'2px'
//                     },
//                     '&:hover fieldset': {
//                         borderColor: style.color1, // Change the border color on hover if needed\
//                         borderWidth:'3px'
                 
                        
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color1, // Change the border color when focused if needed
//                         borderWidth:'3px'
                        
//                     },
//                     '&.Mui-disabled': {
//                         '& fieldset': {
//                         borderColor: style.color1, // Change the border color when disabled
//                         },
//                     },
//                     },
//                     '& .MuiInputLabel-root': {

                    
//                     color: style.color2, // Change the label color here
//                     fontSize:'14pt',
//                     },
//                     '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color1, // Change the label color when focused here
//                     },
//                     '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color1, // Change the label color when disabled
//                     },
//                     '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                         color: style.color1, // Change the input font color when disabled // so it works in safari
//                         WebkitTextFillColor: style.color1, // so it works on chrome
//                         fontSize:'14pt'
//                     },
//                     },
//                     '& .MuiOutlinedInput-input': { color: style.color1,
//         '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px rgb(157, 178, 191) inset',
//             WebkitTextFillColor: style.color1, color:style.color1
//          }, }, 
//                 }}
//                 />

        
//         </div>

//         {(formData.firstName != originalData.firstName || formData.lastName != originalData.lastName || formData.okToEmail != originalData.okToEmail) &&(
//         <div style={{marginTop:'50px', marginBottom:'50px', display:'flex', flex:1}}>
//             <Button onClick={submitChanges} sx={{ my: 2, color:style.color4, display:'block', backgroundColor:style.color2, fontSize:'14pt', width:'100%', maxWidth:'250px', minWidth:'250px', margin:'auto', marginTop:'0px', borderColor:style.color1, borderWidth:'2px', borderStyle:'solid', '&:hover': {backgroundColor: style.color4 }, }}>Submit Changes</Button>
//         </div>
//         )}
//         {(formData.firstName === originalData.firstName && formData.lastName === originalData.lastName && formData.okToEmail === originalData.okToEmail) &&(
//         <div style={{marginTop:'50px', marginBottom:'50px', display:'flex', flex:1}}>
//             <Button disabled onClick={submitChanges} sx={{ my: 2, color:style.color4, display:'block', backgroundColor:style.color5, fontSize:'14pt', width:'100%', maxWidth:'250px', minWidth:'250px', margin:'auto', marginTop:'0px', borderColor:style.color3, borderWidth:'2px', borderStyle:'solid', '&:hover': {backgroundColor: style.color4 }, }}>Submit Changes</Button>
//         </div>
//         )}                          




//     </div>

//     </div>

// </div>
// <div id='modal-root'>
// <ChangePasswordModal isOpen={isPasswordModalOpen} close={closePasswordModal} />
    
// </div>


// </div>


//     )}

        
//         </>

//     )
// }